/**
 * PMS.V2.8 · RBAC V2 · Phase 2 — Roles & Permissions UI refactor.
 *
 * The visible hierarchy is the EXACT current Sidebar (TOP_LEVEL + GROUPS,
 * consumed from components/Layout.jsx) — Top Level → Branch → Sub-group →
 * Function → Applicable actions. No second hard-coded tree. Permission
 * identity comes from the Phase-1 canonical registry (never slug-derived).
 *
 * PHASE 2 IS A UI / PERMISSION-EDITOR ARCHITECTURE PHASE ONLY.
 * All checkbox / bulk interaction mutates a LOCAL EDITOR STATE. Persistence
 * to production role/user grants is DISABLED (Save is preview-only, pending
 * the Owner-authorized migration phase). Review Changes shows the canonical
 * diff. Reloading a role discards local edits and returns to stored grants.
 */
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { getUser } from "@/lib/secureStorage";
import {
  ShieldCheckIcon, MagnifyingGlassIcon, CaretRightIcon, CaretDownIcon, XIcon,
  WarningIcon, FloppyDiskIcon, EyeIcon, ArrowCounterClockwiseIcon, ListChecksIcon,
} from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import {
  buildRbacTree, sectionFunctions, mappedByCode, cloneGrants, grantsFromPermissions,
  countActions, applyToggle, bulkSelectAll, bulkViewOnly, bulkClear, bulkColumn,
  triState, diffGrants, codeIndex, CORE_ACTIONS, ORDERED_ACTIONS, ACTION_LABEL,
  permissionsPayload, registryActions, invalidGrants, sameGrants, lacksView, missingViewFunctions,
} from "@/lib/rbacTree";

// ── Tri-state checkbox ────────────────────────────────────────────────────
function TriBox({ state, disabled, onClick, testId, title }) {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.indeterminate = state === "partial"; }, [state]);
  return (
    <input ref={ref} type="checkbox" data-testid={testId} title={title}
      disabled={disabled || state === "empty"} checked={state === "all"}
      onChange={() => onClick && onClick(state)}
      className="w-4 h-4 accent-[var(--accent)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed" />
  );
}

// ── Branch bulk-control button row ─────────────────────────────────────────
function BulkBar({ scope, onSelectAll, onViewAll, onClear, disabled }) {
  const base = "text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-sm border disabled:opacity-40 disabled:cursor-not-allowed";
  return (
    <div className="flex items-center gap-1.5" onClick={e => e.stopPropagation()}>
      <button data-testid={`bulk-select-all-${scope}`} disabled={disabled} onClick={onSelectAll}
        className={`${base} border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10`} title="Select every applicable action for every function in this branch only">Select All</button>
      <button data-testid={`bulk-view-all-${scope}`} disabled={disabled} onClick={onViewAll}
        className={`${base} border-sky-500/40 text-sky-300 hover:bg-sky-500/10`} title="Add View to every function in this branch only — other selected actions are kept">View All</button>
      <button data-testid={`bulk-clear-${scope}`} disabled={disabled} onClick={onClear}
        className={`${base} border-[var(--border)] text-zinc-400 hover:text-white`} title="Clear every selection in this branch only">Clear All</button>
    </div>
  );
}

const MISSING_VIEW_MSG = "This function may not be accessible through the MES menu or screen without View permission.";

// ── One function's action cells ────────────────────────────────────────────
function FunctionRow({ fn, cols, editor, superAdmin, onToggle, onFnSelectAll }) {
  if (fn.unmapped) {
    return (
      <tr className="border-b border-[var(--border)]/30" data-testid={`rbac-fn-${fn.id}`}>
        <td className="px-3 py-1.5 text-xs">
          <div className="text-zinc-300">{fn.label}{fn.adminOnly && <span className="ml-2 text-[9px] uppercase tracking-widest text-amber-400">admin</span>}</div>
          <div className="text-[10px] text-zinc-600 font-mono">{fn.intendedCode || fn.route}</div>
        </td>
        <td colSpan={cols.length + 1} className="px-3 py-1.5 text-[10px] text-amber-400/80 italic">
          <WarningIcon size={11} weight="bold" className="inline mr-1" />unmapped — no canonical permission (registry alignment pending)
        </td>
      </tr>
    );
  }
  const set = editor[fn.code] || new Set();
  const sel = countActions(editor, fn.code, fn.actions);
  const fnState = superAdmin ? "all" : (sel === 0 ? "none" : sel === fn.actions.length ? "all" : "partial");
  return (
    <tr className="border-b border-[var(--border)]/30 hover:bg-[var(--bg-2)]/30" data-testid={`rbac-fn-${fn.id}`}>
      <td className="px-3 py-1.5 text-xs">
        <div className="text-zinc-200 flex items-center gap-2">
          {fn.label}
          {fn.legacy && <span className="text-[9px] uppercase tracking-widest text-amber-400 border border-amber-500/30 rounded-sm px-1">legacy</span>}
          {fn.shared && <span className="text-[9px] uppercase tracking-widest text-sky-400 border border-sky-500/30 rounded-sm px-1" title="Shared canonical permission identity">shared</span>}
          {fn.structuralCleanup && <span className="text-[9px] uppercase tracking-widest text-fuchsia-400 border border-fuchsia-500/30 rounded-sm px-1" title="Appears in two Sidebar locations — Owner structural cleanup required">dual</span>}
          {fn.adminOnly && <span className="text-[9px] uppercase tracking-widest text-zinc-500">admin</span>}
        </div>
        <div className="text-[10px] text-zinc-600 font-mono">{fn.code}</div>
        {!superAdmin && lacksView(set, fn.actions) && (
          <div data-testid={`missing-view-warning-${fn.code}`} className="mt-0.5 text-[10px] text-amber-300 flex items-start gap-1">
            <WarningIcon size={11} weight="bold" className="mt-px shrink-0" />{MISSING_VIEW_MSG}
          </div>
        )}
      </td>
      {cols.map(a => {
        const allowed = fn.actions.includes(a);
        if (!allowed) return <td key={a} className="px-2 py-1.5 text-center"><span className="inline-block w-3.5 h-3.5 bg-[var(--bg-2)]/60 rounded-sm" title="Not applicable" /></td>;
        return (
          <td key={a} className="px-2 py-1.5 text-center">
            <input type="checkbox" data-testid={`cell-${fn.code}-${a}`}
              checked={superAdmin ? true : set.has(a)} disabled={superAdmin}
              onChange={() => onToggle(fn.code, a, !set.has(a), fn.actions)}
              className="w-3.5 h-3.5 accent-[var(--accent)] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed" />
          </td>
        );
      })}
      <td className="px-2 py-1.5 text-center">
        <TriBox state={fnState} disabled={superAdmin} testId={`fn-all-${fn.code}`} title="Select all applicable actions for this function"
          onClick={(st) => onFnSelectAll(fn, st === "all")} />
      </td>
    </tr>
  );
}

// ── A sub-group table (Level-2) ────────────────────────────────────────────
function SubGroupTable({ sg, cols, editor, superAdmin, onToggle, onFnSelectAll, onColumn, visibleFilter, expanded, toggleExpand }) {
  const fns = sg.functions.filter(visibleFilter);
  if (fns.length === 0) return null;
  const mapped = mappedByCode(fns);
  const hasHeader = !!sg.label;
  const isOpen = !hasHeader || expanded.has(sg.key);
  const st = superAdmin ? "all" : triState(editor, mapped);
  return (
    <div className="border-t border-[var(--border)]/40">
      {hasHeader && (
        <div className="flex items-center justify-between px-3 py-1.5 bg-[var(--bg-2)]/20 cursor-pointer" onClick={() => toggleExpand(sg.key)} data-testid={`rbac-l2-${sg.key}`}>
          <div className="flex items-center gap-2">
            {isOpen ? <CaretDownIcon size={12} className="text-zinc-500" /> : <CaretRightIcon size={12} className="text-zinc-500" />}
            <TriBox state={st} disabled={superAdmin} testId={`l2-tri-${sg.key}`} onClick={(cur) => onColumn.branchTri(mapped, cur)} />
            <span className="text-[11px] font-semibold text-zinc-300 tracking-wide">{sg.label}</span>
          </div>
          <BulkBar scope={`l2-${sg.key}`} disabled={superAdmin}
            onSelectAll={() => onColumn.selectAll(mapped)} onViewAll={() => onColumn.viewAll(mapped)} onClear={() => onColumn.clear(mapped)} />
        </div>
      )}
      {isOpen && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-zinc-500 border-b border-[var(--border)]/40">
                <th className="text-left px-3 py-1.5 font-medium">Function</th>
                {cols.map(a => (
                  <th key={a} className="px-2 py-1.5 text-center font-medium">
                    <button data-testid={`col-${sg.key}-${a}`} disabled={superAdmin} onClick={() => onColumn.column(mapped, a)}
                      className="hover:text-white disabled:opacity-40" title={`Toggle ${ACTION_LABEL[a]} for every applicable function in this sub-group`}>
                      {ACTION_LABEL[a]}
                    </button>
                  </th>
                ))}
                <th className="px-2 py-1.5 text-center font-medium text-zinc-600">All</th>
              </tr>
            </thead>
            <tbody>
              {fns.map(fn => <FunctionRow key={fn.id} fn={fn} cols={cols} editor={editor} superAdmin={superAdmin} onToggle={onToggle} onFnSelectAll={onFnSelectAll} />)}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── A Level-1 branch card ──────────────────────────────────────────────────
function BranchCard({ section, cols, editor, superAdmin, handlers, visibleFilter, expanded, toggleExpand }) {
  const allFns = sectionFunctions(section).filter(visibleFilter);
  if (allFns.length === 0) return null;
  const mapped = mappedByCode(sectionFunctions(section));
  const isOpen = expanded.has(section.key);
  const st = superAdmin ? "all" : triState(editor, mapped);
  const selCount = mapped.reduce((n, f) => n + countActions(editor, f.code, f.actions), 0);
  const totCount = mapped.reduce((n, f) => n + f.actions.length, 0);
  return (
    <div className="tactical-card" data-testid={`rbac-branch-${section.key}`}>
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[var(--border)] cursor-pointer" onClick={() => toggleExpand(section.key)} data-testid={`rbac-l1-${section.key}`}>
        <div className="flex items-center gap-2.5">
          {isOpen ? <CaretDownIcon size={14} className="text-zinc-400" /> : <CaretRightIcon size={14} className="text-zinc-400" />}
          <TriBox state={st} disabled={superAdmin} testId={`l1-tri-${section.key}`} onClick={(cur) => handlers.branchTri(mapped, cur)} />
          <div>
            <div className="font-display text-sm font-bold text-white tracking-tight">{section.label}</div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-mono">{mapped.length} function{mapped.length !== 1 ? "s" : ""} · {superAdmin ? "full (bypass)" : `${selCount}/${totCount}`}</div>
          </div>
        </div>
        <BulkBar scope={`l1-${section.key}`} disabled={superAdmin}
          onSelectAll={() => handlers.selectAll(mapped)} onViewAll={() => handlers.viewAll(mapped)} onClear={() => handlers.clear(mapped)} />
      </div>
      {isOpen && section.subgroups.map(sg => (
        <SubGroupTable key={sg.key} sg={sg} cols={cols} editor={editor} superAdmin={superAdmin}
          onToggle={handlers.toggle} onFnSelectAll={handlers.fnSelectAll} onColumn={handlers}
          visibleFilter={visibleFilter} expanded={expanded} toggleExpand={toggleExpand} />
      ))}
    </div>
  );
}

// ── Role Templates tab ─────────────────────────────────────────────────────
// ── A1-D3 · Legacy / Invalid Permissions panel (explicit, local-only removal) ──
const REASON_LABEL = { unknown_module: "module not in registry", unsupported_action: "action not supported by module",
  non_assignable_alias: "non-assignable alias (expands to other modules)" };
function LegacyInvalidPanel({ invalid, editor, onToggle, onUndo }) {
  const pending = invalid.filter(x => !(editor[x.code] || new Set()).has(x.action));
  return (
    <div className="tactical-card border-l-2 border-l-red-500 p-4" data-testid="legacy-invalid-panel">
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <WarningIcon size={14} weight="bold" className="text-red-400" />
        <span className="font-display text-xs font-bold text-white">Legacy / Invalid Permissions ({invalid.length})</span>
        {pending.length > 0 && <span className="text-[10px] font-mono text-red-300" data-testid="legacy-pending-count">{pending.length} pending removal · not saved</span>}
        {pending.length > 0 && <button data-testid="legacy-undo-removals" onClick={onUndo}
          className="ml-auto h-7 px-2.5 border border-[var(--border)] text-zinc-300 hover:text-white text-[11px] rounded-sm flex items-center gap-1.5"><ArrowCounterClockwiseIcon size={11} weight="bold" />Undo removals</button>}
      </div>
      <div className="text-[11px] text-zinc-500 mb-3">Stored on this role but not valid in the permission registry. Unmarked entries are kept exactly as stored when you save other changes; they cannot be newly granted. Exception: entries marked "{REASON_LABEL.non_assignable_alias}" cannot be kept — they must be removed before the role can be saved. To remove one, mark it, then Review Changes, tick the removal confirmation, and Save. Nothing changes until you save.</div>
      <div className="space-y-1">
        {invalid.map(x => {
          const marked = pending.some(p => p.key === x.key);
          return (
            <div key={x.key} data-testid={`legacy-invalid-row-${x.code}-${x.action}`} className={`flex items-center gap-3 text-[11px] font-mono px-2 py-1 rounded-sm border ${marked ? "border-red-500/50 bg-red-500/10" : "border-[var(--border)]"}`}>
              <span className={marked ? "text-red-300 line-through" : "text-amber-300"}>module <span className="text-white">{x.code}</span> · action <span className="text-white">{x.action}</span></span>
              <span className="text-zinc-500">{REASON_LABEL[x.reason]}</span>
              {marked && <span className="text-[9px] uppercase tracking-widest text-red-400" data-testid={`legacy-pending-${x.code}-${x.action}`}>pending removal</span>}
              <button data-testid={`legacy-remove-toggle-${x.code}-${x.action}`} onClick={() => onToggle(x)}
                className={`ml-auto h-6 px-2 text-[10px] uppercase tracking-widest rounded-sm border ${marked ? "border-[var(--border)] text-zinc-300" : "border-red-500/50 text-red-300 hover:bg-red-500/10"}`}>
                {marked ? "Keep" : "Mark for removal"}</button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function RoleTemplatesTab({ tree, cIndex, roles, registry, registryReady }) {
  const [selectedRole, setSelectedRole] = useState("");
  const [original, setOriginal] = useState({});
  const [editor, setEditor] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [revision, setRevision] = useState(null);
  const [search, setSearch] = useState("");
  const [advanced, setAdvanced] = useState(false);
  const [expanded, setExpanded] = useState(new Set());
  const [reviewOpen, setReviewOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // {label, run}
  const [removalConfirmed, setRemovalConfirmed] = useState(false);

  const superAdmin = selectedRole === "Administrator";
  const cols = advanced ? ORDERED_ACTIONS : CORE_ACTIONS;

  useEffect(() => {
    if (!selectedRole) { const first = roles.find(r => r.code !== "Administrator") || roles[0]; if (first) setSelectedRole(first.code); }
  }, [roles]);

  const loadRole = (code) => {
    if (!code) return;
    if (superAdmin) { setOriginal({}); setEditor({}); setRevision(null); return; }
    setLoading(true); setRemovalConfirmed(false);
    return api.get(`/rbac/roles/${encodeURIComponent(code)}/permissions`)
      .then(({ data }) => { const g = grantsFromPermissions(data.permissions); setOriginal(g); setEditor(cloneGrants(g)); setRevision(data.revision || null); return g; })
      .catch(e => { toast.error(e?.response?.data?.detail || "Failed to load permissions"); return null; })
      .finally(() => setLoading(false));
  };
  useEffect(() => { if (selectedRole === "Administrator") { setOriginal({}); setEditor({}); setRevision(null); } else loadRole(selectedRole); /* eslint-disable-next-line */ }, [selectedRole]);

  const dirty = useMemo(() => { const d = diffGrants(original, editor); return d.added.length + d.removed.length; }, [original, editor]);
  // A1-D3 · invalid stored grants (registry-based) and those the Administrator has explicitly marked for removal.
  const invalid = useMemo(() => (registryReady ? invalidGrants(original, registry) : []), [original, registry, registryReady]);
  const pendingRemovals = useMemo(() => invalid.filter(x => !(editor[x.code] || new Set()).has(x.action)), [invalid, editor]);
  const remainingInvalid = invalid.length - pendingRemovals.length;

  // Stage 2C · persist ONLY the selected role's desired state (authoritative
  // replace). Payload includes legacy/orphan codes → they are preserved.
  const save = async () => {
    if (superAdmin || !selectedRole || !dirty || saving) return;
    if (pendingRemovals.length && !removalConfirmed) return;
    if (pendingRemovals.length && !revision) { toast.error("Role revision unavailable — reload the role before removing legacy permissions"); return; }
    setSaving(true);
    const intended = cloneGrants(editor);
    try {
      const payload = { permissions: permissionsPayload(editor), expected_revision: revision };
      const { data } = await api.put(`/rbac/roles/${encodeURIComponent(selectedRole)}/permissions`, payload);
      const g = parseInt(data?.granted ?? 0, 10), r = parseInt(data?.revoked ?? 0, 10);
      setReviewOpen(false);
      const stored = await loadRole(selectedRole); // reload authoritative stored grants; clears dirty
      const has = (g, c, a) => (g?.[c] || new Set()).has(a);
      const removedOk = stored && pendingRemovals.every(x => !has(stored, x.code, x.action));
      const keptOk = stored && Object.keys(original).every(c => [...original[c]].every(a => !has(intended, c, a) || has(stored, c, a)));
      if (!removedOk || !keptOk) toast.error(`Saved ${selectedRole}, but the reloaded permissions do not match the intended set — review the role`);
      else {
        toast.success(`Saved ${selectedRole} — ${g} granted, ${r} revoked`);
        if (!sameGrants(stored, intended)) {
          const d = diffGrants(intended, stored);
          toast.warning(`Server canonicalised ${d.added.length + d.removed.length} entr${d.added.length + d.removed.length === 1 ? "y" : "ies"} (legacy alias codes): ${[...d.removed.map(x => `−${x.code}:${x.action}`), ...d.added.map(x => `+${x.code}:${x.action}`)].slice(0, 6).join(", ")}`);
        }
      }
    } catch (e) {
      const st = e?.response?.status;
      let det = e?.response?.data?.detail; // api.js stringifies object details (e.g. A1-D2 INVALID_ROLE_PERMISSIONS)
      if (typeof det === "string" && det.startsWith("{")) { try { det = JSON.parse(det); } catch { /* keep string */ } }
      const msg = (typeof det === "string" ? det : det?.message)
        || (st === 403 ? "Not authorized to save permissions"
          : st === 404 ? "Role no longer exists — reload the role list"
          : st === 409 ? "Conflict — this role changed since you loaded it. Reload, review, and save again."
          : st === 422 ? "Invalid permission payload"
          : "Failed to save permissions");
      toast.error(msg); // local edits are intentionally preserved on failure
    } finally { setSaving(false); }
  };

  const mutate = (fn) => { setEditor(prev => { const g = cloneGrants(prev); fn(g); return g; }); };
  const handlers = {
    toggle: (code, a, on) => mutate(g => applyToggle(g, code, a, on)),
    fnSelectAll: (fn, isAll) => mutate(g => { g[fn.code] = isAll ? new Set() : new Set(fn.actions); }),
    selectAll: (m) => mutate(g => bulkSelectAll(g, m)),
    viewAll: (m) => mutate(g => bulkViewOnly(g, m)),
    clear: (m) => mutate(g => bulkClear(g, m)),
    column: (m, a) => mutate(g => {
      const anyOff = m.some(f => f.actions.includes(a) && !(g[f.code] || new Set()).has(a));
      bulkColumn(g, m, a, anyOff);
    }),
    branchTri: (m, cur) => mutate(g => { cur === "all" ? bulkClear(g, m) : bulkSelectAll(g, m); }),
    toggleRemoval: (x) => { setRemovalConfirmed(false); mutate(g => { const st = g[x.code] || new Set(); st.has(x.action) ? st.delete(x.action) : st.add(x.action); g[x.code] = st; }); },
    undoRemovals: () => { setRemovalConfirmed(false); mutate(g => invalid.forEach(x => { (g[x.code] ||= new Set()).add(x.action); })); },
  };

  const globalMapped = useMemo(() => mappedByCode(tree.flatMap(sectionFunctions)), [tree]);
  const gSel = useMemo(() => globalMapped.reduce((n, f) => n + countActions(editor, f.code, f.actions), 0), [globalMapped, editor]);
  const gTot = useMemo(() => globalMapped.reduce((n, f) => n + f.actions.length, 0), [globalMapped]);

  // Grants held by the role that are NOT represented anywhere in the Sidebar
  // tree (legacy / deprecated / orphan modules) — shown read-only so 100% of
  // the role's grants are accounted for (zero unexplained difference).
  const orphanGrants = useMemo(() => {
    const out = [];
    const bad = new Set(invalid.map(x => x.key));
    for (const code in original) { if (!cIndex[code]) original[code].forEach(a => { if (!bad.has(`${code}:${a}`)) out.push(`${code}:${a}`); }); }
    return out.sort();
  }, [original, cIndex, invalid]);

  const q = search.trim().toLowerCase();
  const visibleFilter = (fn) => !q || (`${fn.label} ${fn.code || fn.intendedCode || ""} ${fn.route}`).toLowerCase().includes(q);

  const allKeys = useMemo(() => { const s = new Set(); tree.forEach(sec => { s.add(sec.key); sec.subgroups.forEach(sg => sg.label && s.add(sg.key)); }); return s; }, [tree]);
  const expandAll = () => setExpanded(new Set(allKeys));
  const collapseAll = () => setExpanded(new Set());
  const toggleExpand = (k) => setExpanded(prev => { const s = new Set(prev); s.has(k) ? s.delete(k) : s.add(k); return s; });
  // Auto-expand matches while searching.
  useEffect(() => { if (q) setExpanded(new Set(allKeys)); }, [q, allKeys]);

  const summary = useMemo(() => {
    let viewOnly = 0, full = 0, partial = 0;
    globalMapped.forEach(f => { const s = countActions(editor, f.code, f.actions); if (s === 0) return; if (s === f.actions.length) full++; else if (s === 1 && (editor[f.code] || new Set()).has("view")) viewOnly++; else partial++; });
    return { viewOnly, full, partial };
  }, [globalMapped, editor]);

  return (
    <div className="space-y-4">
      {/* role chips */}
      <div className="flex flex-wrap gap-2 border-b border-[var(--border)] pb-3">
        {roles.map(r => (
          <button key={r.code} data-testid={`role-chip-${r.code}`}
            onClick={() => { if (dirty && !window.confirm("Discard unsaved editor changes?")) return; setSelectedRole(r.code); }}
            className={`px-3 py-1.5 rounded-sm text-xs font-medium border ${selectedRole === r.code ? "border-[var(--accent)] bg-[var(--accent)]/10 text-white" : "border-[var(--border)] text-zinc-400 hover:text-white"}`} title={r.description}>
            {r.name}{r.code === "Administrator" && <span className="ml-2 text-[9px] uppercase tracking-widest text-emerald-400">SUPER</span>}
          </button>
        ))}
      </div>

      {/* Stage 2C · controlled persistence banner */}
      <div className="flex items-center gap-2 text-[11px] text-sky-300 bg-sky-500/10 border border-sky-500/30 rounded-sm px-3 py-2" data-testid="stage2c-persistence-banner">
        <ShieldCheckIcon size={14} weight="bold" />
        Manual permission persistence is <span className="font-semibold">enabled</span> for the selected role only. Edit, <span className="font-semibold">Review Changes</span>, then Save — only this role's grants are written; legacy/unmapped grants are preserved unless you explicitly mark invalid ones for removal.
      </div>

      {superAdmin ? (
        <div className="tactical-card p-5 flex items-center gap-3" data-testid="super-admin-banner">
          <ShieldCheckIcon size={22} weight="bold" className="text-emerald-400" />
          <div>
            <div className="font-display text-sm font-bold text-white">Administrator — Super-admin bypass (read-only)</div>
            <div className="text-xs text-zinc-500">Effective full access via bypass semantics. The matrix below is shown fully granted and read-only — no explicit grants are materialised.</div>
          </div>
        </div>
      ) : (
        <>
          {/* toolbar */}
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <div className="relative">
                <MagnifyingGlassIcon size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-500" />
                <input data-testid="rbac-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search function / permission…"
                  className="h-8 w-56 pl-7 pr-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" />
              </div>
              <button data-testid="expand-all" onClick={expandAll} className="h-8 px-2.5 border border-[var(--border)] text-zinc-300 hover:text-white text-[11px] rounded-sm">Expand All</button>
              <button data-testid="collapse-all" onClick={collapseAll} className="h-8 px-2.5 border border-[var(--border)] text-zinc-300 hover:text-white text-[11px] rounded-sm">Collapse All</button>
              <label className="flex items-center gap-1.5 text-[11px] text-zinc-400 ml-1">
                <input type="checkbox" data-testid="advanced-actions" checked={advanced} onChange={e => setAdvanced(e.target.checked)} className="w-3.5 h-3.5 accent-[var(--accent)]" />
                Advanced actions
              </label>
            </div>
            <div className="flex items-center gap-2">
              <button data-testid="global-view-all" title="Add View to every function — other selected actions are kept" onClick={() => handlers.viewAll(globalMapped)}
                className="h-8 px-3 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 text-xs font-semibold rounded-sm flex items-center gap-1.5"><EyeIcon size={12} weight="bold" />View All</button>
              <button data-testid="global-select-all" onClick={() => setConfirm({ label: "Select ALL applicable permissions across every function for this role (local editor only)?", run: () => handlers.selectAll(globalMapped) })}
                className="h-8 px-3 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-xs font-semibold rounded-sm flex items-center gap-1.5"><ListChecksIcon size={12} weight="bold" />Select All</button>
              <button data-testid="global-clear-all" onClick={() => setConfirm({ label: "Clear ALL editable permissions for this role (local editor only)?", run: () => handlers.clear(globalMapped) })}
                className="h-8 px-3 border border-[var(--border)] text-zinc-400 hover:text-white text-xs font-semibold rounded-sm">Clear All</button>
            </div>
          </div>

          {/* role summary */}
          <div className="flex items-center gap-4 text-[11px] font-mono text-zinc-500 flex-wrap" data-testid="role-summary">
            <span>Role: <span className="text-white">{roles.find(r => r.code === selectedRole)?.name || selectedRole}</span></span>
            <span>Granted: <span className="text-white">{gSel}/{gTot}</span></span>
            <span>Full: <span className="text-emerald-300">{summary.full}</span></span>
            <span>View-only: <span className="text-sky-300">{summary.viewOnly}</span></span>
            <span>Partial: <span className="text-amber-300">{summary.partial}</span></span>
            {dirty > 0 && <span className="text-[var(--accent)]" data-testid="unsaved-indicator">● {dirty} unsaved change{dirty !== 1 ? "s" : ""}</span>}
            <div className="ml-auto flex items-center gap-2">
              <button data-testid="reload-role" onClick={() => { if (dirty && !window.confirm("Discard local edits and reload stored grants?")) return; loadRole(selectedRole); }}
                className="h-8 px-3 border border-[var(--border)] text-zinc-300 hover:text-white text-xs rounded-sm flex items-center gap-1.5"><ArrowCounterClockwiseIcon size={12} weight="bold" />Reload</button>
              <button data-testid="review-changes" disabled={!dirty} onClick={() => setReviewOpen(true)}
                className="h-8 px-3 border border-[var(--accent)]/50 text-[var(--accent)] hover:bg-[var(--accent)]/10 disabled:opacity-40 text-xs font-semibold rounded-sm">Review Changes</button>
              <button data-testid="save-permissions" disabled={!dirty || saving} onClick={() => (pendingRemovals.length ? setReviewOpen(true) : save())}
                title={!dirty ? "No unsaved changes" : "Persist this role's permission changes"}
                className="h-8 px-3 bg-[var(--accent)] text-white hover:bg-blue-600 disabled:bg-[var(--bg-2)] disabled:text-zinc-500 disabled:cursor-not-allowed text-xs font-semibold rounded-sm flex items-center gap-1.5">
                <FloppyDiskIcon size={12} weight="bold" />{saving ? "Saving…" : "Save"}</button>
            </div>
          </div>
        </>
      )}

      {loading ? (
        <div className="p-8 text-center text-zinc-500 text-sm">Loading permissions…</div>
      ) : (
        <div className="space-y-3" data-testid="rbac-tree">
          {tree.map(section => (
            <BranchCard key={section.key} section={section} cols={cols} editor={editor} superAdmin={superAdmin}
              handlers={handlers} visibleFilter={visibleFilter} expanded={expanded} toggleExpand={toggleExpand} />
          ))}
        </div>
      )}

      {!superAdmin && !loading && invalid.length > 0 && (
        <LegacyInvalidPanel invalid={invalid} editor={editor} onToggle={handlers.toggleRemoval} onUndo={handlers.undoRemovals} />
      )}

      {/* Legacy / orphan diagnostic panel */}
      {!superAdmin && orphanGrants.length > 0 && (
        <div className="tactical-card border-l-2 border-l-amber-500 p-4" data-testid="orphan-grants-panel">
          <div className="flex items-center gap-2 mb-2"><WarningIcon size={14} weight="bold" className="text-amber-400" />
            <span className="font-display text-xs font-bold text-white">Legacy / unmapped grants held by this role</span></div>
          <div className="text-[11px] text-zinc-500 mb-2">These canonical permissions are held by the role but are NOT part of the Sidebar matrix above (deprecated / orphan modules). They are shown for completeness and are retained unchanged — no migration in Phase 2.</div>
          <div className="flex flex-wrap gap-1.5">
            {orphanGrants.map(k => <span key={k} className="text-[10px] font-mono text-amber-300 border border-amber-500/30 rounded-sm px-1.5 py-0.5">{k}</span>)}
          </div>
        </div>
      )}

      <ReviewDialog open={reviewOpen} onClose={() => { setReviewOpen(false); setRemovalConfirmed(false); }} role={selectedRole} original={original} editor={editor} cIndex={cIndex} onSave={save} saving={saving}
        missingView={superAdmin ? [] : missingViewFunctions(editor, globalMapped)}
        legacy={{ pending: pendingRemovals, remaining: remainingInvalid, invalidKeys: new Set(invalid.map(x => x.key)), confirmed: removalConfirmed, setConfirmed: setRemovalConfirmed }} />

      <Dialog open={!!confirm} onOpenChange={() => setConfirm(null)}>
        <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-md" description="Bulk permission editor action (local only).">
          <DialogHeader><DialogTitle className="font-display">Confirm bulk action</DialogTitle>
            <DialogDescription className="text-xs text-zinc-500">{confirm?.label}</DialogDescription></DialogHeader>
          <DialogFooter>
            <button onClick={() => setConfirm(null)} className="h-9 px-4 border border-[var(--border)] text-zinc-300 hover:text-white text-sm rounded-sm">Cancel</button>
            <button data-testid="confirm-bulk" onClick={() => { confirm.run(); setConfirm(null); }} className="h-9 px-4 bg-[var(--accent)] hover:bg-blue-600 text-white text-sm font-semibold rounded-sm">Apply (local)</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Review Changes dialog ──────────────────────────────────────────────────
// A1-D3 · removal confirmation block inside Review.
function LegacyRemovalReview({ legacy, editor }) {
  const remainingValid = permissionsPayload(editor).filter(p => !legacy.invalidKeys.has(`${p.module_code}:${p.action}`))
    .map(p => `${p.module_code}:${p.action}`).sort();
  return (
    <div className="space-y-3 border-t border-[var(--border)] pt-3" data-testid="review-legacy-section">
      <div>
        <div className="overline text-red-400 mb-1">Invalid legacy permissions to remove ({legacy.pending.length})</div>
        {legacy.pending.map(x => <div key={x.key} data-testid={`review-legacy-remove-${x.code}-${x.action}`} className="text-[11px] font-mono text-red-300">− module {x.code} · action {x.action}</div>)}
      </div>
      {legacy.remaining > 0 && <div className="text-[11px] text-amber-300" data-testid="review-legacy-remaining-warning">{legacy.remaining} invalid permission{legacy.remaining !== 1 ? "s" : ""} not marked — kept exactly as stored (not newly granted).</div>}
      <div>
        <div className="overline text-zinc-400 mb-1">Valid permissions that will remain ({remainingValid.length})</div>
        <div className="max-h-32 overflow-y-auto flex flex-wrap gap-1" data-testid="review-remaining-valid">
          {remainingValid.length === 0 ? <span className="text-[11px] text-zinc-500">None</span>
            : remainingValid.map(k => <span key={k} className="text-[10px] font-mono text-zinc-300 border border-[var(--border)] rounded-sm px-1">{k}</span>)}
        </div>
      </div>
      <label className="flex items-start gap-2 text-[11px] text-zinc-300">
        <input type="checkbox" data-testid="review-confirm-removal" checked={legacy.confirmed} onChange={e => legacy.setConfirmed(e.target.checked)} className="mt-0.5 w-3.5 h-3.5 accent-[var(--accent)]" />
        I confirm removal of the {legacy.pending.length} invalid legacy permission{legacy.pending.length !== 1 ? "s" : ""} listed above.
      </label>
    </div>
  );
}

function MissingViewReview({ codes, cIndex }) {
  return (
    <div data-testid="review-missing-view" className="border border-amber-500/30 bg-amber-500/5 rounded-sm p-2">
      <div className="overline text-amber-400 mb-1">Without View ({codes.length}) · advisory</div>
      <div className="text-[11px] text-amber-300/90 mb-1">{MISSING_VIEW_MSG}</div>
      {codes.map(c => <div key={c} data-testid={`review-missing-view-${c}`} className="text-[11px] font-mono text-amber-300">! {c} <span className="text-zinc-600">· {cIndex[c]?.label || "?"}</span></div>)}
    </div>
  );
}

function ReviewDialog({ open, onClose, role, original, editor, cIndex, onSave, saving, legacy, missingView }) {
  const { added, removed } = useMemo(() => diffGrants(original, editor), [original, editor]);
  const branches = useMemo(() => {
    const s = new Set();
    [...added, ...removed].forEach(x => s.add(cIndex[x.code]?.branch || "Other"));
    return Array.from(s);
  }, [added, removed, cIndex]);
  const noChanges = added.length === 0 && removed.length === 0;
  const needsConfirm = legacy.pending.length > 0 && !legacy.confirmed;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-[var(--bg-1)] border-[var(--border)] text-white max-w-lg" description="Review the canonical permission changes staged in the local editor.">
        <DialogHeader>
          <DialogTitle className="font-display">Review Changes — {role}</DialogTitle>
          <DialogDescription className="text-xs text-zinc-500">
            {added.length} added · {removed.length} removed · {branches.length} branch{branches.length !== 1 ? "es" : ""} affected. Review, then Save to persist this role's grants.
          </DialogDescription>
        </DialogHeader>
        <div className="max-h-[50vh] overflow-y-auto space-y-3">
          {added.length === 0 && removed.length === 0 && <div className="text-xs text-zinc-500 py-6 text-center">No changes staged.</div>}
          {added.length > 0 && (
            <div>
              <div className="overline text-emerald-400 mb-1">Added ({added.length})</div>
              {added.map(x => <div key={`a${x.code}${x.action}`} data-testid={`diff-add-${x.code}-${x.action}`} className="text-[11px] font-mono text-emerald-300">+ {x.code}:{x.action} <span className="text-zinc-600">· {cIndex[x.code]?.branch || "?"}</span></div>)}
            </div>
          )}
          {removed.length > 0 && (
            <div>
              <div className="overline text-red-400 mb-1">Removed ({removed.length})</div>
              {removed.map(x => <div key={`r${x.code}${x.action}`} data-testid={`diff-remove-${x.code}-${x.action}`} className="text-[11px] font-mono text-red-300">− {x.code}:{x.action} <span className="text-zinc-600">· {cIndex[x.code]?.branch || "?"}</span></div>)}
            </div>
          )}
          {legacy.pending.length > 0 && <LegacyRemovalReview legacy={legacy} editor={editor} />}
          {missingView.length > 0 && <MissingViewReview codes={missingView} cIndex={cIndex} />}
        </div>
        <DialogFooter>
          {needsConfirm && <div data-testid="review-save-needs-confirm" className="text-[11px] text-amber-300 mr-auto self-center">Permissions are being removed — tick the removal confirmation above to enable Save.</div>}
          <button data-testid="review-close" onClick={onClose} className="h-9 px-4 border border-[var(--border)] text-zinc-300 hover:text-white text-sm rounded-sm flex items-center gap-2"><XIcon size={12} weight="bold" />Close</button>
          <button data-testid="review-save" disabled={noChanges || saving || needsConfirm} onClick={onSave}
            title={noChanges ? "No changes to save" : needsConfirm ? "Permissions are being removed — tick the removal confirmation to enable Save" : "Persist this role's permission changes"}
            className="h-9 px-4 bg-[var(--accent)] text-white hover:bg-blue-600 disabled:bg-[var(--bg-2)] disabled:text-zinc-500 disabled:cursor-not-allowed text-sm font-semibold rounded-sm flex items-center gap-2"><FloppyDiskIcon size={12} weight="bold" />{saving ? "Saving…" : "Save"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── User Overrides tab (read-only display · no persistence in Phase 2) ──────
function UserOverridesTab({ tree, cIndex }) {
  const [users, setUsers] = useState([]);
  const [uid, setUid] = useState("");
  const [roleGrants, setRoleGrants] = useState({});
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => { api.get("/rbac/users").then(({ data }) => setUsers(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    if (!uid) { setRoleGrants({}); setOverrides({}); return; }
    setLoading(true);
    api.get(`/rbac/users/${uid}/permissions`)
      .then(({ data }) => { setRoleGrants(grantsFromPermissions(data.role_permissions)); setOverrides(grantsFromPermissions(data.overrides)); })
      .catch(e => toast.error(e?.response?.data?.detail || "Failed to load user permissions"))
      .finally(() => setLoading(false));
  }, [uid]);

  const q = search.toLowerCase();
  const fUsers = users.filter(u => !q || `${u.email} ${u.name || ""} ${u.role || ""}`.toLowerCase().includes(q));
  const sel = users.find(u => u.id === uid);
  const flatSet = (g) => { const s = new Set(); for (const c in g) g[c].forEach(a => s.add(`${c}:${a}`)); return s; };
  const inherited = useMemo(() => flatSet(roleGrants), [roleGrants]);
  const overSet = useMemo(() => flatSet(overrides), [overrides]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-4">
      <div className="tactical-card p-3 space-y-2 self-start lg:sticky lg:top-4">
        <div className="overline">Users</div>
        <input data-testid="user-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Search…" className="h-8 w-full px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" />
        <div className="max-h-[60vh] overflow-y-auto flex flex-col gap-0.5">
          {fUsers.map(u => (
            <button key={u.id} data-testid={`user-chip-${u.id}`} onClick={() => setUid(u.id)}
              className={`text-left px-2 py-1.5 rounded-sm border ${u.id === uid ? "border-[var(--accent)] bg-[var(--accent)]/10" : "border-transparent hover:bg-[var(--bg-2)]/40"}`}>
              <div className="text-xs text-white truncate">{u.email}</div>
              <div className="text-[10px] text-zinc-500 font-mono uppercase tracking-widest">{u.role || "—"}</div>
            </button>
          ))}
          {fUsers.length === 0 && <div className="text-xs text-zinc-500 py-4 text-center">No users</div>}
        </div>
      </div>
      <div className="space-y-3">
        <div className="flex items-center gap-2 text-[11px] text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-sm px-3 py-2">
          <WarningIcon size={14} weight="bold" />
          Read-only display. Phase 2 does not persist override changes. Backend override model is ADDITIVE only — there is no explicit per-user DENY (reported as a limitation).
        </div>
        {!uid && <div className="tactical-card p-8 text-center text-zinc-500 text-sm">Select a user to view inherited role grants and per-user overrides.</div>}
        {uid && (
          <>
            <div className="tactical-card p-3 flex items-center justify-between flex-wrap gap-2">
              <div><div className="text-sm font-semibold text-white">{sel?.email}</div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Role: <span className="text-zinc-300">{sel?.role || "—"}</span>
                  <span className="ml-4">Inherited: <span className="text-emerald-300">{inherited.size}</span></span>
                  <span className="ml-4">Overrides: <span className="text-[var(--accent)]">{overSet.size}</span></span></div></div>
            </div>
            {loading ? <div className="p-8 text-center text-zinc-500 text-sm">Loading…</div> : (
              <>
                <div className="tactical-card p-4">
                  <div className="overline text-emerald-400 mb-2">Inherited from role ({inherited.size})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {[...inherited].sort().map(k => <span key={k} className="text-[10px] font-mono text-emerald-300 border border-emerald-500/30 rounded-sm px-1.5 py-0.5">{k}</span>)}
                    {inherited.size === 0 && <span className="text-xs text-zinc-500">None</span>}
                  </div>
                </div>
                <div className="tactical-card p-4">
                  <div className="overline text-[var(--accent)] mb-2">Directly added for user ({overSet.size})</div>
                  <div className="flex flex-wrap gap-1.5">
                    {[...overSet].sort().map(k => <span key={k} className="text-[10px] font-mono text-[var(--accent)] border border-[var(--accent)]/30 rounded-sm px-1.5 py-0.5">{k}</span>)}
                    {overSet.size === 0 && <span className="text-xs text-zinc-500">None</span>}
                  </div>
                </div>
              </>
            )}
          </>
        )}
      </div>
    </div>
  );
}

// ── Page shell ─────────────────────────────────────────────────────────────
export default function RolesPermissions() {
  const me = getUser();
  const isAdmin = me?.role === "Administrator";
  const [modules, setModules] = useState([]);
  const [roles, setRoles] = useState([]);
  const [subTab, setSubTab] = useState(() => { try { return localStorage.getItem("mes.rbac.subtab") || "templates"; } catch { return "templates"; } });
  const setTab = (k) => { setSubTab(k); try { localStorage.setItem("mes.rbac.subtab", k); } catch { /* ignore */ } };

  useEffect(() => {
    (async () => {
      try {
        const [m, r] = await Promise.all([api.get("/rbac/modules"), api.get("/rbac/roles")]);
        setModules(m.data || []); setRoles(r.data || []);
      } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load RBAC data"); }
    })();
  }, []);

  const tree = useMemo(() => buildRbacTree(modules), [modules]);
  const cIndex = useMemo(() => codeIndex(tree), [tree]);
  const registry = useMemo(() => registryActions(modules), [modules]);
  const fnCount = useMemo(() => tree.reduce((n, s) => n + sectionFunctions(s).length, 0), [tree]);
  const mappedCount = useMemo(() => mappedByCode(tree.flatMap(sectionFunctions)).length, [tree]);

  if (!isAdmin) return <Navigate to="/" replace />;

  return (
    <div className="p-6 space-y-5" data-testid="rbac-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-1">Access Control</div>
          <h1 className="font-display text-3xl font-black tracking-tight text-white flex items-center gap-3">
            <ShieldCheckIcon size={26} weight="bold" className="text-[var(--accent)]" />Roles &amp; Permissions
          </h1>
          <div className="text-xs text-zinc-500 mt-1">Sidebar-mirrored hierarchy · {fnCount} function nodes ({mappedCount} canonical) · {roles.length} role{roles.length === 1 ? "" : "s"} — manual permission persistence (per selected role).</div>
        </div>
      </div>

      <div className="flex gap-2 border-b border-[var(--border)]">
        {[{ key: "templates", label: "Role Templates" }, { key: "users", label: "User Overrides" }].map(t => (
          <button key={t.key} data-testid={`rbac-subtab-${t.key}`} onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 text-sm font-medium border-b-2 -mb-px ${subTab === t.key ? "border-[var(--accent)] text-white" : "border-transparent text-zinc-400 hover:text-white"}`}>{t.label}</button>
        ))}
      </div>

      {subTab === "templates" && <RoleTemplatesTab tree={tree} cIndex={cIndex} roles={roles} registry={registry} registryReady={modules.length > 0} />}
      {subTab === "users" && <UserOverridesTab tree={tree} cIndex={cIndex} />}
    </div>
  );
}
